import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { get } from "lib/api";
import { firstListMediaUrl, firstThumbnailMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import useStorefrontHero from "../../shared/useStorefrontHero";
import StorefrontLayout from "../components/StorefrontLayout";
import { resolvePrice, VBS, btnPrimary, btnSecondary, btnOnDark, labelCaps } from "../theme";

import { ProductCardRow } from "../components/ProductCard";

export default function HomePage() {
  const navigate = useNavigate();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const { addItem } = useCart();
  const { t } = useStorefrontCopy();
  const canOrder = !!token;
  const companyKey = String(storefrontTenant || activeCompany || "vbsus").toLowerCase();
  const { imageUrl: heroImage } = useStorefrontHero(companyKey);

  const trustItems = [
    { stat: t("home.trustStatQc", "QC"), label: t("home.trustQc", "Documented calibration on every unit") },
    { stat: t("home.trustStatCore", "CORE"), label: t("home.trustCore", "Core return program with clear tracking") },
    { stat: t("home.trustStatWarranty", "Warranty"), label: t("home.trustWarranty", "Register valve bodies in your portal") },
    { stat: t("home.trustStatSupport", "Support"), label: t("home.trustSupport", "Shop-direct technical assistance") },
  ];

  const { data: productsData, isLoading } = useQuery({
    queryKey: ["vbsus-home-products", companyKey],
    queryFn: () =>
      get("/api/storefront/catalog", {
        sort: "updatedAt:desc",
        pagination: { pageSize: 12 },
      }),
    staleTime: 30000,
  });

  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    if (!productsData?.data) return;
    setFeaturedProducts(
      productsData.data.map((item) => {
        const attrs = item.attributes || item;
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          sku: attrs.sku,
          name: attrs.name,
          images: attrs.images,
          unitPrice: attrs.unitPrice,
          priceLevels: attrs.priceLevels,
          status: attrs.status,
          transmissions: attrs.transmissions,
        };
      }),
    );
  }, [productsData]);

  const currency =
    productsData?.meta?.company?.baseCurrency || currencyForCompany(companyKey);

  const openProduct = (product) => navigate(`/catalog/${product.documentId}`);

  const addProduct = (product) => {
    const price = Number(resolvePrice(product) || 0);
    if (!price) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: price,
        coreCharge: product.coreCharge != null ? Number(product.coreCharge) : null,
        imageUrl: firstThumbnailMediaUrl(product?.images) || firstListMediaUrl(product?.images),
      },
      1,
    );
  };

  return (
    <StorefrontLayout maxWidth={null}>
      {/* Full-bleed image + overlapping content card (not split-column hero) */}
      <Box sx={{ bgcolor: VBS.canvas, pb: { xs: 4, md: 5 } }}>
        <Box
          sx={{
            position: "relative",
            height: { xs: 260, sm: 340, md: 440 },
            overflow: "hidden",
            bgcolor: VBS.ink,
          }}
        >
          {heroImage ? (
            <Box
              component="img"
              src={heroImage}
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 40%",
                display: "block",
                opacity: 0.92,
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                background: `repeating-linear-gradient(
                  -45deg,
                  ${VBS.inkSoft},
                  ${VBS.inkSoft} 12px,
                  ${VBS.ink} 12px,
                  ${VBS.ink} 24px
                )`,
              }}
            />
          )}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: {
                xs: "linear-gradient(180deg, rgba(15,15,18,0.35) 0%, rgba(15,15,18,0.2) 38%, rgba(15,15,18,0.88) 100%)",
                md: "linear-gradient(105deg, rgba(15,15,18,0.78) 0%, rgba(15,15,18,0.28) 48%, rgba(15,15,18,0.62) 100%)",
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 5,
              background: VBS.swoosh,
            }}
          />
        </Box>

        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 2,
            mt: { xs: -7, sm: -8, md: -9 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              bgcolor: VBS.surface,
              border: `1px solid ${VBS.lineStrong}`,
              borderTop: "none",
              borderRadius: `${VBS.radiusMd}px`,
              boxShadow: VBS.shadowLift,
              p: { xs: 3, md: 4 },
              maxWidth: 680,
              overflow: "hidden",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: VBS.swoosh,
              },
            }}
          >
            <Box sx={labelCaps} component="p">
              {t("home.eyebrow", "Premium remanufactured valve bodies")}
            </Box>
            <Box
              component="h1"
              sx={{
                m: "0.65rem 0 0",
                fontSize: { xs: "1.875rem", sm: "2.25rem", md: "2.75rem" },
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
                color: VBS.ink,
              }}
            >
              {t("home.heroTitle", "Clean shifts start with controlled hydraulics.")}
            </Box>
            <Box
              component="p"
              sx={{
                mt: 1.5,
                mb: 0,
                fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                lineHeight: 1.65,
                color: VBS.muted,
              }}
            >
              {t(
                "home.heroSubtitle",
                "Valve bodies rebuilt with measured wear correction, precise calibration, and a workflow built for repeatable results.",
              )}
            </Box>
            <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1.25 }}>
              <Box component="button" type="button" onClick={() => navigate("/catalog")} sx={btnPrimary}>
                {t("home.primaryCta", "Shop products")}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => navigate(token ? "/account" : "/register")}
                sx={btnSecondary}
              >
                {token ? t("home.myAccount", "My account") : t("home.openAccount", "Open account")}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Trust strip */}
      <Box sx={{ bgcolor: VBS.panel, borderBottom: `1px solid ${VBS.line}` }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Grid container spacing={2}>
            {trustItems.map((item) => (
              <Grid item xs={6} md={3} key={item.stat}>
                <Box
                  sx={{
                    bgcolor: VBS.surface,
                    border: `1px solid ${VBS.lineStrong}`,
                    borderRadius: `${VBS.radiusMd}px`,
                    p: 2,
                    height: "100%",
                    borderTop: "none",
                    overflow: "hidden",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: VBS.swoosh,
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: VBS.fontMono,
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      color: VBS.accent,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {item.stat}
                  </Box>
                  <Box
                    component="p"
                    sx={{ m: "0.5rem 0 0", fontSize: "0.8125rem", lineHeight: 1.5, color: VBS.muted }}
                  >
                    {item.label}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Horizontal product rail — EDS uses static 4-col grid */}
      <Box sx={{ py: { xs: 5, md: 6 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 2.5, gap: 2 }}>
            <Box>
              <Box sx={labelCaps} component="p">
                {t("home.featuredEyebrow", "In stock & ready")}
              </Box>
              <Box
                component="h2"
                sx={{ m: "0.35rem 0 0", fontSize: "1.5rem", fontWeight: 800, color: VBS.ink }}
              >
                {t("home.featuredTitle", "Shop valve bodies")}
              </Box>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => navigate("/catalog")}
              sx={{
                margin: 0,
                border: 0,
                bgcolor: "transparent",
                color: VBS.accent,
                fontWeight: 700,
                fontSize: "0.8125rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              {t("home.featuredLink", "Full catalog →")}
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ py: 6, textAlign: "center", color: VBS.muted }}>
              {t("home.loadingProducts", "Loading products…")}
            </Box>
          ) : featuredProducts.length ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                pb: 1,
                scrollSnapType: "x mandatory",
                mx: -1,
                px: 1,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": { bgcolor: VBS.lineStrong, borderRadius: 999 },
              }}
            >
              {featuredProducts.slice(0, 10).map((product) => (
                <ProductCardRow
                  key={product.documentId}
                  product={product}
                  currency={currency}
                  onOpen={() => openProduct(product)}
                  onAdd={addProduct}
                  canOrder={canOrder}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ py: 6, textAlign: "center", color: VBS.muted }}>
              {t("catalog.empty", "No products are available right now.")}
            </Box>
          )}
        </Container>
      </Box>

      {/* Full-width orange CTA band */}
      <Box sx={{ bgcolor: VBS.accent, color: "#fff", py: { xs: 5, md: 6 } }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: { xs: "1.5rem", md: "1.875rem" },
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            {t("home.ctaBandTitle", "Built for transmission shops")}
          </Box>
          <Box
            component="p"
            sx={{
              mt: 1.25,
              mb: 3,
              fontSize: "1rem",
              lineHeight: 1.65,
              color: "rgba(255,255,255,0.88)",
              maxWidth: 480,
              mx: "auto",
            }}
          >
            {t(
              "home.ctaBandBody",
              "Order parts, register units, manage cores, and open warranty claims — all from one account.",
            )}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "center" }}>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(token ? "/account" : "/authentication/sign-in")}
              sx={btnOnDark}
            >
              {token ? t("home.goToPortal", "Go to portal") : t("home.secondaryCta", "Sign in")}
            </Box>
            {!token ? (
              <Box
                component="button"
                type="button"
                onClick={() => navigate("/register")}
                sx={{
                  ...btnOnDark,
                  bgcolor: "transparent",
                  color: "#fff",
                  border: "2px solid rgba(255,255,255,0.65)",
                }}
              >
                {t("home.createAccount", "Create account")}
              </Box>
            ) : null}
          </Box>
        </Container>
      </Box>
    </StorefrontLayout>
  );
}
